import React from 'react';

import type * as hiveCore from '@gitbook/hive-core';
import type { EditorContainer } from '@gitbook/slate-react';

import { useNavigationState } from '../../../../navigation';

/**
 * Hook to handle focusing the page editor when the page changes.
 *
 * If the page is editable, this hook will either focus the editor
 * or the page title depending on whether or not there is a selection
 * in the editor document.
 */
export function useFocusPage(props: {
    editorRef: React.RefObject<EditorContainer>;
    isReadOnly: boolean;
    page: hiveCore.RevisionPageDocument;
    pageTitleRef: React.RefObject<HTMLTextAreaElement>;
}) {
    const { editorRef, isReadOnly, pageTitleRef, page } = props;

    const navigationState = useNavigationState();

    React.useEffect(() => {
        if (!page.id) {
            return;
        }

        const editor = editorRef.current;
        if (!editor) {
            return;
        }

        const shouldBeFocused = !navigationState.overlay && !isReadOnly;
        if (!shouldBeFocused) {
            return;
        }

        // if no selection is set and there is a page title, set the focus to it.
        if (editor.value.isSelectionUnset && pageTitleRef.current) {
            pageTitleRef.current.focus();
            pageTitleRef.current.setSelectionRange(
                pageTitleRef.current.value.length,
                pageTitleRef.current.value.length
            );
            return;
        }

        // if we already have a selection or there is no other focus point (e.g. hidden page title), focus the editor
        if (editor) {
            editor.change((change) => {
                if (editor.value.isSelectionUnset) {
                    change.collapseToStartOf(editor.value.document); // if there is no selection, collapse to the start of the document
                }
                change.focus(); // focus the editor to restore the selection after the page change
            });
        }
    }, [page.id, editorRef, isReadOnly, navigationState.overlay, pageTitleRef]);
}
